export interface C2PAManifestAction {
  action: string;
  description: string;
  iconText: string;
  iconName: string;
  title: string;
}

export interface C2PAManifestIngredient {
  thumbnail: string;
  title: string;
}

// The title and description data for majority of these fields are from the following:
// https://c2pa.org/specifications/specifications/1.4/ux/UX_Recommendations.html#_user_facing_edit_and_activity_labels_and_descriptions
// The following titles and descriptions are unofficial: c2pa.delete, c2pa.edited.metadata, c2pa.font.*, c2pa.redacted
const Actions: C2PAManifestAction[] = [
  {
    action: 'c2pa.color_adjustments',
    description: 'Adjusted properties like tone, saturation, curves, shadows or highlights',
    iconText: 'Palette',
    iconName: 'palette',
    title: 'Colour or exposure edits',
  },
  {
    action: 'c2pa.converted',
    description: 'Changed file format with transcoding',
    iconText: 'Converted',
    iconName: 'switch-flip',
    title: 'Converted',
  },
  {
    action: 'c2pa.created',
    description: 'Created a new file or content',
    iconText: 'Add page',
    iconName: 'page-add',
    title: 'Created',
  },
  {
    action: 'c2pa.cropped',
    description: 'Used cropping tools, reducing or expanding visible content area',
    iconText: 'Crop',
    iconName: 'crop',
    title: 'Cropping edits',
  },
  {
    action: 'c2pa.deleted',
    description: 'Deleted parts of the content',
    iconText: 'Trash',
    iconName: 'trash',
    title: 'Deleted',
  },
  {
    action: 'c2pa.drawing',
    description: 'Used tools like pencils, brushes, erasers, or shape, path, or pen tools',
    iconText: 'Drawing tools',
    iconName: 'pencil-paintbrush-pen',
    title: 'Drawing edits',
  },
  {
    action: 'c2pa.edited',
    description: 'Performed other edits which may or may not change appearance',
    iconText: 'Edit',
    iconName: 'edit-thin',
    title: 'Other edits',
  },
  {
    action: 'c2pa.edited.metadata',
    description: 'Performed edits to the asset metadata which do not change content',
    iconText: 'Browser code',
    iconName: 'browser-code-alt',
    title: 'Metadata edits',
  },
  {
    action: 'c2pa.filtered',
    description: 'Used tools like filters, styles or effects',
    iconText: 'Magic wand',
    iconName: 'magic-wand-black',
    title: 'Filter or style edits',
  },
  {
    action: 'c2pa.font.charactersAdded',
    description: 'Added font characters or character sets',
    iconText: 'Text',
    iconName: 'typography',
    title: 'Added font characters',
  },
  {
    action: 'c2pa.font.charactersDeleted',
    description: 'Removed font characters or character sets',
    iconText: 'Text',
    iconName: 'typography',
    title: 'Removed font characters',
  },
  {
    action: 'c2pa.font.charactersModified',
    description: 'Added and removed font characters or character sets',
    iconText: 'Text',
    iconName: 'typography',
    title: 'Modified font characters',
  },
  {
    action: 'c2pa.font.createdFromVariableFont',
    description: 'Created a font, partially or completely, from a variable font',
    iconText: 'Text in square',
    iconName: 'type-square',
    title: 'Created font from variable font',
  },
  {
    action: 'c2pa.font.edited',
    description: 'Performed other font edits',
    iconText: 'Text',
    iconName: 'typography',
    title: 'Other font edits',
  },
  {
    action: 'c2pa.font.hinted',
    description: 'Applied font hinting',
    iconText: 'Text',
    iconName: 'typography',
    title: 'Hinted font',
  },
  {
    action: 'c2pa.font.merged',
    description: 'Created a font with a combination of pre-existing fonts',
    iconText: 'Text in square',
    iconName: 'type-square',
    title: 'Merged fonts',
  },
  {
    action: 'c2pa.font.openTypeFeatureAdded',
    description: 'Added a font OpenType feature',
    iconText: 'Text sizes',
    iconName: 'format-size',
    title: 'Added font OpenType feature',
  },
  {
    action: 'c2pa.font.openTypeFeatureModified',
    description: 'Altered a font OpenType feature',
    iconText: 'Text sizes',
    iconName: 'format-size',
    title: 'Altered font OpenType feature',
  },
  {
    action: 'c2pa.font.openTypeFeatureRemoved',
    description: 'Removed a font OpenType feature',
    iconText: 'Text sizes',
    iconName: 'format-size',
    title: 'Removed font OpenType feature',
  },
  {
    action: 'c2pa.font.subset',
    description: 'Simplified a font to support an arbitrary subset of characters',
    iconText: 'Text in square',
    iconName: 'type-square',
    title: 'Created font subset',
  },
  {
    action: 'c2pa.opened',
    description: 'Opened a pre-existing file',
    iconText: 'Share',
    iconName: 'share',
    title: 'Opened',
  },
  {
    action: 'c2pa.orientation',
    description: 'Changed position or orientation (rotated, flipped, etc.)',
    iconText: 'Rotate',
    iconName: 'rotate',
    title: 'Changed orientation',
  },
  {
    action: 'c2pa.placed',
    description: 'Added pre-existing content to this file',
    iconText: 'Import',
    iconName: 'send-down',
    title: 'Imported',
  },
  {
    action: 'c2pa.published',
    description: 'Distributed this file on an online platform',
    iconText: 'Publish file',
    iconName: 'file-upload',
    title: 'Published',
  },
  {
    action: 'c2pa.redacted',
    description: 'Redacted one or more assertions',
    iconText: 'Redacted file',
    iconName: 'page-cancel',
    title: 'Redacted',
  },
  {
    action: 'c2pa.removed',
    description: 'Removed content or files created or added during the editing process',
    iconText: 'Removed file',
    iconName: 'page-minus',
    title: 'Removed',
  },
  {
    action: 'c2pa.repackaged',
    description: 'Changed container file format without transcoding',
    iconText: 'Rotate',
    iconName: 'rotate',
    title: 'Repackaged',
  },
  {
    action: 'c2pa.resized',
    description: 'Changed dimensions or file size',
    iconText: 'Expand',
    iconName: 'expand',
    title: 'Resizing edits',
  },
  {
    action: 'c2pa.transcoded',
    description: 'Converted from one file encoding to another, potentially affecting properties like resolution scale, bitrate, or encoding format',
    iconText: 'Rotate',
    iconName: 'rotate',
    title: 'Transcoded',
  },
  {
    action: 'c2pa.unknown',
    description: 'Performed edits or activity that couldn\'t be recognized',
    iconText: 'Question mark',
    iconName: 'help',
    title: 'Unknown edits or activity',
  },
];

const MonthsOfYear = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export class C2PAManifest {
  actions: C2PAManifestAction[];
  appsOrDevices: string[];
  authors: string[];
  certificateSerial: string;
  ingredients: C2PAManifestIngredient[];
  issuer: string;
  issuedOn: Date;
  issuedOnString: string;
  issuedOnDateFormatted: string;
  title: string;

  constructor(manifest: any) {
    // Actions array
    this.actions = [];

    // Signed manifests label it c2pa.actions.v2; an exact match never found it
    const actionsAssertion = (manifest?.assertions as any[])?.find(assertion => assertion.label?.startsWith('c2pa.actions'));
    const actions = (actionsAssertion?.data?.actions as any[])?.map(action => action.action);
    if (actions?.length) {
      new Set(actions).forEach(action => {
        const newAction = Actions.find(a => a.action === action);
        if (newAction) this.actions.push(newAction);
      });
    }

    // Apps or devices array
    if (manifest?.claim_generator_info?.length) this.appsOrDevices = (manifest?.claim_generator_info as any[]).map(i => i.name);
    else {
      const claimGeneratorFormatted = manifest?.claim_generator?.split(' ')[0].replaceAll('_', ' ').replaceAll('/', ' ');
      this.appsOrDevices = [claimGeneratorFormatted ?? ''];
    }

    // Authors array
    this.authors = ((manifest?.assertions as any[])?.find(assertion => assertion.label === 'stds.schema-org.CreativeWork')
      ?.data?.author as any[])?.map(a => a.name) ?? [];

    // Ingredients array
    this.ingredients = (manifest?.ingredients as any[])?.map(ingredient => ({
      title: ingredient?.title,
      thumbnail: ingredient?.thumbnail?.identifier ?? '/assets/images/defaults/default-media-picture.png',
    } as C2PAManifestIngredient)) ?? [];

    // Issued date information
    this.certificateSerial = manifest?.signature_info?.certificate_serial ?? '';
    this.issuer = manifest?.signature_info?.issuer;
    this.issuedOn = new Date(manifest?.signature_info?.time);
    this.issuedOnString = manifest?.signature_info?.time;
    if (this.issuedOn && !isNaN(this.issuedOn.getTime())) {
      const pad = (value: number) => String(value).padStart(2, '0');
      this.issuedOnDateFormatted = `${this.issuedOn.getUTCDate()} ${MonthsOfYear[this.issuedOn.getUTCMonth()]} ${this.issuedOn.getUTCFullYear()}` +
        ` at ${pad(this.issuedOn.getUTCHours())}:${pad(this.issuedOn.getUTCMinutes())} +00:00`;
    }
    else this.issuedOnDateFormatted = 'Unknown';

    // Title
    this.title = manifest?.title;
  }
}
